import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, X, ArrowRight } from "lucide-react";

const STORAGE_KEY = "rf_yearly_upsell_last_shown";
const RENOUNCE_KEY = "rf_yearly_upsell_dismissed";
const SHOW_EVERY_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

export default function AnnualUpsellModal() {
    const { user, refresh } = useAuth();
    const [visible, setVisible] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [interval, setIntervalName] = useState(null);

    useEffect(() => {
        if (!user || !["solo", "agency", "pro"].includes(user.tier)) return;
        if (localStorage.getItem(RENOUNCE_KEY) === "1") return;
        const last = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
        if (Date.now() - last < SHOW_EVERY_MS) return;
        // Only show for monthly interval
        client.get("/billing/current-plan").then((r) => {
            if (r.data.interval === "month" && !r.data.cancel_at_period_end) {
                setIntervalName("month");
                setVisible(true);
                localStorage.setItem(STORAGE_KEY, String(Date.now()));
            }
        }).catch(() => {});
    }, [user]);

    if (!visible || interval !== "month") return null;

    const dismiss = (persist = false) => {
        setVisible(false);
        if (persist) localStorage.setItem(RENOUNCE_KEY, "1");
    };

    const switchYearly = async () => {
        setSwitching(true);
        try {
            const r = await client.post("/billing/switch-to-yearly", { origin_url: window.location.origin });
            if (r.data.already_yearly) {
                toast.info("You're already on the yearly plan");
            } else {
                toast.success("Switched to yearly plan — savings applied!");
                if (refresh) await refresh();
            }
            setVisible(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Switch failed");
        } finally {
            setSwitching(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-background/70 backdrop-blur-sm px-4" data-testid="annual-upsell-modal">
            <div className="relative border border-primary/70 bg-card p-8 max-w-md w-full brand-glow">
                <button onClick={() => dismiss(false)} data-testid="upsell-close-btn"
                        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 border border-primary grid place-items-center">
                        <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <div className="overline text-primary">Loyalty offer</div>
                </div>
                <h2 className="font-display text-3xl tracking-tight">
                    Save 17% by going yearly.
                </h2>
                <p className="text-sm text-muted-foreground mt-3">
                    You're currently on the <span className="text-foreground">monthly</span> Pro plan.
                    Switch to the annual plan — you'll pay <span className="text-foreground font-mono">$290/year</span>
                    {" "}instead of <span className="text-foreground font-mono">$348</span> and get 2 months free.
                    We'll pro-rate your current period automatically.
                </p>
                <div className="mt-6 border-t border-border/60 pt-4 space-y-1 text-xs text-muted-foreground">
                    <div>· Same Pro features · same billing account</div>
                    <div>· Cancel anytime from the billing portal</div>
                    <div>· Pro-ration applied on next invoice</div>
                </div>
                <div className="mt-8 flex gap-2 flex-wrap">
                    <button data-testid="upsell-switch-btn" onClick={switchYearly} disabled={switching}
                            className="flex-1 bg-primary text-primary-foreground px-4 py-3 font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                        {switching ? "Switching…" : (<>Switch & save <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                    <button data-testid="upsell-later-btn" onClick={() => dismiss(false)}
                            className="px-4 py-3 text-sm text-muted-foreground hover:text-foreground border border-border">
                        Not now
                    </button>
                </div>
                <button data-testid="upsell-never-btn" onClick={() => dismiss(true)}
                        className="mt-3 text-xs text-muted-foreground/70 hover:text-muted-foreground w-full text-center">
                    Don't show this again
                </button>
            </div>
        </div>
    );
}
